import React, { FC, useMemo, useEffect, useRef } from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { cn } from '@/lib/utils';
import type { ButtonGroupProps } from 'react-multi-carousel';
import { useAppSelector } from 'store/hooks';
import { programColumnsSelector } from 'store/programs/programs.selector';

interface Props extends ButtonGroupProps {
  addColumn: () => void;
}

const CarouselNav: FC<Props> = ({ addColumn, goToSlide, next, previous, ...rest }) => {
  const columns = useAppSelector(programColumnsSelector);
  const {
    carouselState: { currentSlide, totalItems },
  } = rest;

  const prevColumnCount = useRef<number>(0);

  const columnCount = useMemo(() => {
    return Object.keys(columns).length;
  }, [columns]);

  // Handle navigation when column count decreases and current slide is beyond new total
  useEffect(() => {
    if (prevColumnCount.current > 0 && columnCount < prevColumnCount.current) {
      // Column was removed
      if (currentSlide >= columnCount && goToSlide) {
        // Current slide is beyond the new total, navigate to the last available slide
        const newLastSlide = Math.max(0, columnCount - 1);
        goToSlide(newLastSlide);
      }
    }
    prevColumnCount.current = columnCount;
  }, [columnCount, currentSlide, goToSlide]);

  const handleAddColumn = () => {
    addColumn();
    if (goToSlide) {
      goToSlide(totalItems); // Navigate to the newly added column
    }
  };

  return (
    <div className={'absolute bottom-0 w-full'}>
      <div className={'flex h-fit w-full items-center justify-between bg-[#0949ee] px-3 py-3'}>
        {/* LEFT NAV */}
        <div className={'flex h-8 max-w-[80px] items-center gap-1'}>
          <div
            onClick={currentSlide === 0 ? undefined : previous}
            className={cn(
              'flex items-center justify-center rounded-md bg-[#FEFEFE]/20 p-1 text-white',
              currentSlide === 0 ? 'opacity-50' : 'opacity-100'
            )}>
            <KeyboardArrowLeftIcon />
          </div>
          <div
            onClick={currentSlide === totalItems - 1 ? undefined : next}
            className={cn(
              'flex items-center justify-center rounded-md bg-[#FEFEFE]/20 p-1 text-white',
              currentSlide === totalItems - 1 ? 'opacity-50' : 'opacity-100'
            )}>
            <KeyboardArrowRightIcon />
          </div>
        </div>
        {/* RIGHT ACTION BUTTONS */}
        <div className={'flex h-fit w-fit flex-row-reverse items-center gap-1'}>
          <div
            onClick={handleAddColumn}
            className={cn(
              'flex h-9 w-fit items-center justify-center rounded-md px-4 py-2 font-semibold text-nowrap',
              'max-w-[120px] cursor-pointer bg-white text-sm text-[#0949ee]'
            )}>
            Add new
          </div>
          <div
            className={cn(
              'flex h-9 w-fit items-center justify-center text-nowrap text-white',
              'max-w-[100px] p-2 font-semibold'
            )}>
            {columnCount} columns
          </div>
        </div>
      </div>
    </div>
  );
};
export default CarouselNav;
